type DepthRange = { depthFrom: number; depthTo: number };

/**
 * Assigns each valid interval to an overlap track using a greedy
 * interval-partitioning strategy. Intervals that touch (existing depthTo ≤
 * next depthFrom) may share the same track.
 *
 * @param items Intervals to assign to tracks.
 * @param getDepthRange Accessor for the depth range of each item.
 * @param getSortKey Optional tiebreaker key for stable sort ordering.
 * @returns Map from each input item to its track index and the total track count.
 */
export function assignOverlapTracks<T>(
  items: T[],
  getDepthRange: (item: T) => DepthRange,
  getSortKey?: (item: T) => string | number | undefined,
): Map<T, TrackAssignment> {
  const result = new Map<T, TrackAssignment>();

  if (items.length === 0) return result;

  const validItems = items.filter((i) => {
    const { depthFrom, depthTo } = getDepthRange(i);
    return (
      Number.isFinite(depthFrom) &&
      Number.isFinite(depthTo) &&
      depthFrom < depthTo
    );
  });

  if (validItems.length === 0) return result;

  const sorted = [...validItems].sort((a, b) => {
    const da = getDepthRange(a),
      db = getDepthRange(b);
    const byFrom = da.depthFrom - db.depthFrom;
    if (byFrom !== 0) return byFrom;
    const byTo = da.depthTo - db.depthTo;
    if (byTo !== 0) return byTo;
    if (getSortKey) {
      const ka = getSortKey(a),
        kb = getSortKey(b);
      if (ka !== undefined && kb !== undefined) {
        if (typeof ka === "string" && typeof kb === "string")
          return ka.localeCompare(kb);
        return Number(ka) - Number(kb);
      }
    }
    return 0;
  });

  const trackEnds: number[] = [];

  for (const item of sorted) {
    const { depthFrom, depthTo } = getDepthRange(item);
    let assignedTrack = -1;

    for (let t = 0; t < trackEnds.length; t++) {
      if (trackEnds[t] <= depthFrom) {
        assignedTrack = t;
        trackEnds[t] = depthTo;
        break;
      }
    }

    if (assignedTrack === -1) {
      assignedTrack = trackEnds.length;
      trackEnds.push(depthTo);
    }

    result.set(item, { trackIndex: assignedTrack, maxTrackCount: 0 });
  }

  const maxTrackCount = trackEnds.length;
  for (const assignment of result.values()) {
    assignment.maxTrackCount = maxTrackCount;
  }

  return result;
}

export interface TrackAssignment {
  trackIndex: number;
  maxTrackCount: number;
}
